<script lang="ts">
	import { afterUpdate, onMount } from 'svelte';
	import CurrencyRow from 'components/CurrencyRow/CurrencyRow.svelte';
	import RotateIcon from 'assets/svg/rotateIcon.svg?url';
	import NoIcon from 'assets/svg/noIcon.svg?component';
	import type { IStats } from 'customTypes/coinDetails';
	import DollarIcon from 'assets/svg/usFlagIcon.svg?url';
	import EuroIcon from 'assets/svg/euroIcon.svg?url';
	import EthereumIcon from 'assets/svg/ethereum.svg?url';
	import BitcoinIcon from 'assets/svg/bitcoin.svg?url';
	import { get } from 'svelte/store';
	import activeCurrency from 'stores/activeCurrency';
	import { onErrorCoinLogo } from 'utils/onErrorCoinLogo';

	export let data: IStats;
	export let dark: boolean;
	let isSame = false;

	const checkSame = () => {
		isSame = data.symbol === currencyData.currency.toUpperCase();
	};

	const getIconUrl = (currency) => {
		switch (currency) {
			case 'usd':
				return DollarIcon;
			case 'eth':
				return EthereumIcon;
			case 'eur':
				return EuroIcon;
			case 'btc':
				return BitcoinIcon;
			default:
				return DollarIcon;
		}
	};

	let currencyData = get(activeCurrency);
	$: currencyData = $activeCurrency;
	let fromCurrency = { currency: data.symbol, rate: Number(data.price), logo_url: data.logo_url };
	let toCurrency = {
		currency: currencyData.currency.toUpperCase(),
		rate: 1 / Number(data.price) / currencyData.price,
		logo_url: getIconUrl(currencyData.currency),
	};
	let amount = 1;
	let isAmountFromInput = true;
	let exchangeRate = Number(data.price);
	let toAmount: number;
	let fromAmount: number;
	let isInputOnFocus = false;

	const setCurrencyData = () => {
		checkSame();
		fromCurrency = {
			currency: data.symbol,
			rate: Number(data.price) * currencyData.price,
			logo_url: data.logo_url,
		};
		toCurrency = {
			currency: currencyData.currency.toUpperCase(),
			rate: 1 / Number(data.price) / currencyData.price,
			logo_url: getIconUrl(currencyData.currency),
		};
	};

	function handleOnChangeFrom(e: Event & { target: HTMLInputElement }) {
		amount = Number(e.target.value);
		isAmountFromInput = true;
	}
	function handleOnChangeTo(e: Event & { target: HTMLInputElement }) {
		amount = Number(e.target.value);
		isAmountFromInput = false;
	}

	const updateCurrency = () => {
		if (isAmountFromInput && amount) {
			exchangeRate = toCurrency.rate;
			fromAmount = amount;
			toAmount = formatPrice(amount / (isSame ? 1 : exchangeRate));
		} else {
			exchangeRate = toCurrency.rate;
			toAmount = amount;
			fromAmount = formatPrice(amount * (isSame ? 1 : exchangeRate));
		}
	};

	function formatPrice(result: number) {
		let raw = Number(result);
		return raw < 1 ? Number(raw.toFixed(5)) : Number(raw.toFixed(2));
	}

	function handleRotate() {
		[fromCurrency, toCurrency] = [toCurrency, fromCurrency];
		checkSame();
	}

	afterUpdate(() => updateCurrency());
	$: amount;
	$: $activeCurrency, setCurrencyData(), updateCurrency();
</script>

<div class="wrapper" class:dark>
	<h3 class="title">
		{`${fromCurrency.currency} to ${toCurrency.currency} converter`}
	</h3>
	{#if data}
		<div class="converter">
			<CurrencyRow
				{dark}
				bind:selectedValue={fromCurrency}
				bind:amount={fromAmount}
				onChange={handleOnChangeFrom}
				class="mb-mobile"
				label="input"
				bind:isInputOnFocus
			>
				{#if fromCurrency.logo_url}
					<img
						src={fromCurrency.logo_url}
						alt="currency logo"
						class="currency-icon center circle"
						width="20"
						height="20"
					/>
				{:else}
					<NoIcon class="currency-icon center" width="20" height="20" />
				{/if}
			</CurrencyRow>

			<div class="rotate" on:click={handleRotate}>
				<img src={RotateIcon} class="center" alt="rotate" width="16" height="16" />
			</div>

			<CurrencyRow
				{dark}
				bind:selectedValue={toCurrency}
				bind:amount={toAmount}
				onChange={handleOnChangeTo}
				label="output"
				bind:isInputOnFocus
			>
				{#if toCurrency.logo_url}
					<img
						src={toCurrency.logo_url}
						alt="currency logo"
						class="currency-icon center circle"
						width="20"
						height="20"
						on:error={onErrorCoinLogo}
					/>
				{:else}
					<NoIcon class="currency-icon center" width="20" height="20" />
				{/if}
			</CurrencyRow>
		</div>
	{/if}
</div>

<style lang="scss">
	@import 'converter';
</style>
